import { Stack, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useEffect, useRef, } from 'react';
import CartProductSection from './CartProductCategory/CartProductSection/CartProductSection';
import CartProductCategory from './CartProductCategory/CartProductCategory';
import CartProductBattery from './CartProductBattery/CartProductBattery';
import CartProductBodyColor from './CartProductBodyColor/CartProductBodyColor';
import CartProductLiftKit from './CartProductLiftKit/CartProductLiftKit';
import CartProductWheels from './CartProductWheels/CartProductWheels';
import CartProductSeats from './CartProductSeats/CartProductSeats';
import CartProductLsvKit from './CartProductLsvKit/CartProductLsvKit';
import { useFormContext, useWatch } from 'react-hook-form';
import CartProductSummary from './CartProductSummary/CartProductSummary';
import CartProductAccessories from './CartProductAccessories/CartProductAccessories';
import useAuth from '../../../../hooks/useAuth';
import useCartModelDisplay from '../../../../hooks/useCartModelDisplay';

interface CartProductsProps {
  fromInventory: boolean,
  editingCart: boolean,
  autoScroll?: boolean,
  retailConfigurator: boolean,
  modelViewHeight: number,
  powerSectionRef?: React.Ref<HTMLDivElement> | undefined,
  exteriorSectionRef?: React.LegacyRef<HTMLDivElement> | undefined,
  interiorSectionRef?: React.LegacyRef<HTMLDivElement> | undefined,
  accessoriesSectionRef?: React.LegacyRef<HTMLDivElement> | undefined,
  lsvSectionRef?: React.LegacyRef<HTMLDivElement> | undefined,
  summarySectionRef?: React.LegacyRef<HTMLDivElement> | undefined,
  hideAllPrices: boolean
}

const CartProducts: FC<CartProductsProps> = ({
  fromInventory,
  editingCart,
  autoScroll,
  retailConfigurator,
  modelViewHeight,
  powerSectionRef,
  exteriorSectionRef,
  interiorSectionRef,
  accessoriesSectionRef,
  lsvSectionRef,
  summarySectionRef,
  hideAllPrices
}) => {
  const { user } = useAuth();
  const { chassisType } = useCartModelDisplay();
  //TEMP remove hasLicenses to prevent LSV orders Per Erica by setting false
  const hasLicenses = false //user?.companyAttributes?.licenses !== undefined && user?.companyAttributes?.licenses?.length > 0;
  const { getValues, formState: { touchedFields } } = useFormContext();
  const productBatterySectionRef = useRef<HTMLDivElement>(null);
  const productBodyColorSectionRef = useRef<HTMLDivElement>(null);
  const productLiftKitSectionRef = useRef<HTMLDivElement>(null);
  const productWheelsSectionRef = useRef<HTMLDivElement>(null);
  const productSeatsSectionRef = useRef<HTMLDivElement>(null);
  const accessoriesIncludedRef = useRef<HTMLDivElement>(null);
  const accessoriesOptionalRef = useRef<HTMLDivElement>(null);
  const productLsvKitSectionRef = useRef<HTMLDivElement>(null);
  const productsSummarySectionRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.between('laptop', 'laptopL'));
  const isTablet = useMediaQuery(theme.breakpoints.between('tabletS', 'laptop'));
  const isMobile = useMediaQuery(theme.breakpoints.down('tabletS'));

  // Handle resetting page scrolling after refresh
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [])

  const scrollIntoView = (element: HTMLDivElement | undefined) => {
    if (autoScroll && element) {
      const position = element.getBoundingClientRect();
      const offset = isLaptop || isTablet || isMobile ? modelViewHeight + 35 : 35;
      window.scrollTo({
        behavior: 'smooth',
        top: position.top + window.scrollY - offset
      });
    }
  }

  // Handle scrolling to Body Color section
  // after battery select
  const batteryProductSku = useWatch({ name: 'chassisBatteryConfig.sku' });
  const initBatteryProductSku = useRef<string>(getValues('chassisBatteryConfig.sku'));
  useEffect(() => {
    if (batteryProductSku &&
      productBodyColorSectionRef &&
      productBodyColorSectionRef.current &&
      touchedFields?.chassisBatteryConfig &&
      batteryProductSku !== initBatteryProductSku.current) {
      scrollIntoView(productBodyColorSectionRef.current);
      initBatteryProductSku.current = batteryProductSku;
    }
  }, [
    touchedFields,
    batteryProductSku,
    initBatteryProductSku,
    productBodyColorSectionRef
  ]);

  // Handle scrolling to Lift Kit section
  // after body color select
  const bodyColorSku = useWatch({ name: 'bodyColor.sku' });
  const initBodyColorSku = useRef<string>(getValues('bodyColor.sku'));
  useEffect(() => {
    if (bodyColorSku &&
      productLiftKitSectionRef &&
      productLiftKitSectionRef.current &&
      touchedFields?.bodyColor &&
      bodyColorSku !== initBodyColorSku.current) {
      scrollIntoView(productLiftKitSectionRef.current);
      initBodyColorSku.current = bodyColorSku;
    }
  }, [
    touchedFields,
    bodyColorSku,
    initBodyColorSku,
    productLiftKitSectionRef
  ]);

  // Handle scrolling to Wheels section
  // after lift kit selected
  const liftKitSku = useWatch({ name: 'liftKit.sku' });
  const initLiftKitSku = useRef<string>(getValues('liftKit.sku'));
  useEffect(() => {
    if (liftKitSku &&
      productWheelsSectionRef &&
      productWheelsSectionRef.current &&
      touchedFields?.liftKit &&
      liftKitSku !== initLiftKitSku.current) {
      scrollIntoView(productWheelsSectionRef.current);
      initLiftKitSku.current = liftKitSku;
    }
  }, [
    touchedFields,
    liftKitSku,
    initLiftKitSku,
    productWheelsSectionRef
  ]);

  // Handle scrolling to Seats section
  // after wheels selected
  const wheelsSku = useWatch({ name: 'wheel.sku' });
  const initWheelsSku = useRef<string>(getValues('wheel.sku'));
  useEffect(() => {
    if (wheelsSku &&
      productSeatsSectionRef &&
      productSeatsSectionRef.current &&
      touchedFields?.wheel &&
      wheelsSku !== initWheelsSku.current) {
      scrollIntoView(productSeatsSectionRef.current);
      initWheelsSku.current = wheelsSku;
    }
  }, [
    touchedFields,
    wheelsSku,
    initWheelsSku,
    productSeatsSectionRef
  ]);

  // Handle scrolling to Lsv Kit section
  // after seats selected
  const seatSku = useWatch({ name: 'frontSeat.sku' });
  const initSeatSku = useRef<string>(getValues('frontSeat.sku'));
  useEffect(() => {
    if (seatSku &&
      productLsvKitSectionRef &&
      productLsvKitSectionRef.current &&
      touchedFields?.frontSeat &&
      seatSku !== initSeatSku.current) {
      scrollIntoView(productLsvKitSectionRef.current);
      initSeatSku.current = seatSku;
    }
  }, [
    touchedFields,
    seatSku,
    initSeatSku,
    productLsvKitSectionRef
  ]);

  return (
    <Stack
      id="productOptions"
      direction="column"
      spacing={4}
      sx={(theme) => ({
        padding: '10px 10px 10px 10px',
        ...(retailConfigurator && {
          paddingBottom: '120px',
          [theme.breakpoints.down('tabletS')]: {
            paddingLeft: '4px',
            paddingRight: '4px'
          }
        })
      })}>
      <CartProductCategory ref={powerSectionRef} title='Power'>
        <CartProductSection
          ref={productBatterySectionRef} title="Battery">
          <CartProductBattery
            retailConfigurator={retailConfigurator}
            hideAllPrices={hideAllPrices} />
        </CartProductSection>
      </CartProductCategory>
      <CartProductCategory ref={exteriorSectionRef} title='Exterior'>
        <Stack spacing={10}>
          <CartProductSection ref={productBodyColorSectionRef} title="Body Color">
            <CartProductBodyColor
              retailConfigurator={retailConfigurator}
              fromInventory={fromInventory} />
          </CartProductSection>
          <CartProductSection ref={productLiftKitSectionRef} title="Lift Kit">
            <CartProductLiftKit
              retailConfigurator={retailConfigurator} hideAllPrices={hideAllPrices} />
          </CartProductSection>
          <CartProductSection ref={productWheelsSectionRef} title="Wheels">
            <CartProductWheels retailConfigurator={retailConfigurator} hideAllPrices={hideAllPrices} />
          </CartProductSection>
        </Stack>
      </CartProductCategory>
      <CartProductCategory ref={interiorSectionRef} title='Interior'>
        <CartProductSection ref={productSeatsSectionRef} title="Seats">
          <CartProductSeats
            retailConfigurator={retailConfigurator} hideAllPrices={hideAllPrices} />
        </CartProductSection>
      </CartProductCategory>
      {retailConfigurator &&
        <CartProductCategory ref={accessoriesSectionRef} title='Accessories'>
          <Stack spacing={10}>
            <CartProductSection ref={accessoriesIncludedRef} title="Included">
              <CartProductAccessories retailConfigurator={retailConfigurator} accessoriesIncluded={true} hideAllPrices={hideAllPrices} />
            </CartProductSection>
            {(chassisType?.optionalAccessories && chassisType?.optionalAccessories.length > 0) &&
              <CartProductSection ref={accessoriesOptionalRef} title="Optional">
                <CartProductAccessories retailConfigurator={retailConfigurator} accessoriesIncluded={false} hideAllPrices={hideAllPrices} />
              </CartProductSection>
            }
          </Stack>
        </CartProductCategory>
      }
      {(!retailConfigurator && hasLicenses) &&
        <CartProductCategory ref={lsvSectionRef} title='LSV'>
          <CartProductSection ref={productLsvKitSectionRef} title="LSV Kit">
            <CartProductLsvKit retailConfigurator={retailConfigurator} />
          </CartProductSection>
        </CartProductCategory>
      }
      <CartProductCategory ref={summarySectionRef} title='Summary'>
        <CartProductSummary
          ref={productsSummarySectionRef}
          retailConfigurator={retailConfigurator}
          fromInventory={fromInventory}
          editingCart={editingCart}
          hideAllPrices={hideAllPrices} />
      </CartProductCategory>
    </Stack>
  );
}

export default CartProducts;
